// A named import, NOT `import * as Sentry`. The namespace form defeats
// Rollup's tree-shaking and drags Tracing, Replay and Feedback into the shared
// vendor chunk: measured at +145 kB gzipped versus +27 kB for this line
// (StreamCommand-i3cp). Nothing here is worth 145 kB on an OBS overlay.
import { init } from "@sentry/browser";

// Client-side error reporting (StreamCommand-i3cp).
//
// Configuration arrives as <meta> tags rendered by ErrorReportingMetaComponent
// rather than compiled into the bundle: public/vite/ is built once in the
// Dockerfile and shared by staging and production.
//
// No DSN means no init, which is how dev and test stay silent — the server
// initializer's enabled_environments list is not duplicated here.

export function readConfig(doc = document) {
  const meta = (name) => doc.querySelector(`meta[name="${name}"]`)?.content || null;
  return {
    dsn: meta("sentry-dsn"),
    environment: meta("sentry-environment"),
    release: meta("sentry-release"),
    tenant: meta("sentry-tenant"),
  };
}

// Chat message text passes through console on both the dashboard and the
// overlay, and console breadcrumbs would attach viewers' chat to every event.
//
// Dropping them here rather than by omitting the SDK's consoleIntegration:
// that integration was reshuffled between SDK v10 and v11, whereas
// beforeBreadcrumb is version-independent and directly testable.
export function dropConsoleBreadcrumbs(breadcrumb) {
  return breadcrumb?.category === "console" ? null : breadcrumb;
}

// `surface` is the bundle's own identity — "dashboard", "overlay" or
// "obs-bridge" — not the deploy environment. Tagging it separately is what
// makes an overlay crash mid-broadcast distinguishable from a dashboard error,
// which is the whole point of the bean; overloading `environment` would break
// release health instead.
export function sentryOptions(surface, config) {
  const tags = { surface };
  if (config.tenant) tags.tenant = config.tenant;

  return {
    dsn: config.dsn,
    environment: config.environment || undefined,
    release: config.release || undefined,
    // No user identification anywhere: an OBS overlay is viewed by anonymous
    // members of the public, and none of them consented to this.
    sendDefaultPii: false,
    initialScope: { tags },
    beforeBreadcrumb: dropConsoleBreadcrumbs,
  };
}

// Errors only. Tracing and Session Replay are deliberately absent: both add
// continuous network chatter to a page running live during a broadcast, which
// is the exact scenario this exists to protect. Request-path tracing is already
// covered server-side.
//
// Returns whether reporting was actually turned on, so callers (and the test)
// can tell "configured" from "no DSN" without inspecting the SDK.
export function initErrorReporting(surface, { sentryInit = init, doc = document } = {}) {
  const config = readConfig(doc);
  if (!config.dsn) return false;

  sentryInit(sentryOptions(surface, config));
  return true;
}
